<script setup>
// SZ: banner que aparece entre o ChatListHeader e os chips Minhas/NãoLidas/Menções
// quando há um filtro de etiqueta/canal/time ativo (vindo da sidebar).
// Mostra "X conversas em [pill]" + botão X pra limpar.
import { computed } from 'vue';

const props = defineProps({
  // 'label' | 'inbox' | 'team'
  type: { type: String, required: true },
  label: { type: Object, default: null },
  inbox: { type: Object, default: null },
  team: { type: Object, default: null },
  count: { type: Number, default: 0 },
});

const emit = defineEmits(['close']);

const labelText = computed(() => {
  const n = props.count || 0;
  return `${n} conversa${n === 1 ? '' : 's'} em`;
});

const pillName = computed(() => {
  if (props.type === 'label') return props.label?.title || '';
  if (props.type === 'inbox') return props.inbox?.name || '';
  if (props.type === 'team') return props.team?.name || '';
  return '';
});

// Cor da pill:
//  - label: cor da etiqueta (label.color) com alpha 15% no bg
//  - inbox: verde fixo (mesma do sz-search-inbox-tag do search dropdown)
//  - team:  cinza neutro
// Dark mode tratado via override em solarz_override.css por modifier de classe.
const baseColor = computed(() => {
  if (props.type === 'label' && props.label?.color) return props.label.color;
  return null;
});

const pillStyle = computed(() => {
  if (props.type !== 'label' || !baseColor.value) return null; // usa CSS dos modifiers
  return {
    background: `${baseColor.value}26`,
    color: baseColor.value,
  };
});
const dotStyle = computed(() => {
  if (props.type !== 'label' || !baseColor.value) return null;
  return { background: baseColor.value };
});

const pillClass = computed(() => ({
  'sz-afb-pill--inbox': props.type === 'inbox',
  'sz-afb-pill--team': props.type === 'team',
}));
</script>

<template>
  <div class="sz-afb">
    <div class="sz-afb-content">
      <span class="sz-afb-label">{{ labelText }}</span>
      <span class="sz-afb-pill" :class="pillClass" :style="pillStyle">
        <!-- Bolinha colorida APENAS quando é etiqueta (que tem cor própria) -->
        <span v-if="type === 'label'" class="sz-afb-pill-dot" :style="dotStyle"></span>
        <span class="sz-afb-pill-text">{{ pillName }}</span>
      </span>
    </div>
    <button
      type="button"
      class="sz-afb-close"
      :aria-label="$t ? $t('CHAT_LIST.CLEAR_FILTER') : 'Limpar filtro'"
      @click="emit('close')"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  </div>
</template>

<style scoped>
.sz-afb {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  gap: 12px;
  min-height: 48px;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
}
.sz-afb-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.sz-afb-label {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #525252;
  flex-shrink: 0;
  white-space: nowrap;
}
.sz-afb-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px 2px 4px;
  gap: 4px;
  border-radius: 999px;
  min-width: 0;
  max-width: 200px;
  overflow: hidden;
}
/* Inbox (canal) — badge verde igual ao sz-search-inbox-tag do search dropdown */
.sz-afb-pill.sz-afb-pill--inbox {
  padding: 1px 6px;
  border-radius: 4px;
  background: #E8F5E9;
  color: #2E7D32;
}
/* Team — badge cinza neutro */
.sz-afb-pill.sz-afb-pill--team {
  padding: 1px 6px;
  border-radius: 4px;
  background: #F3F4F6;
  color: #525252;
}
.sz-afb-pill-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sz-afb-pill-text {
  font-family: inherit;
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sz-afb-pill.sz-afb-pill--inbox .sz-afb-pill-text,
.sz-afb-pill.sz-afb-pill--team .sz-afb-pill-text {
  font-size: 12px;
  font-weight: 600;
}
.sz-afb-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: 1.5px solid #D4D4D4;
  border-radius: 8px;
  cursor: pointer;
  color: #000000;
  transition: background 0.15s, border-color 0.15s;
}
.sz-afb-close:hover { background: rgba(0, 0, 0, 0.04); }
.sz-afb-close:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
}
</style>
